/** Max age for survey_readiness before the Data Intake Surveys card shows a stale warning. */
export const SURVEY_READINESS_MAX_AGE_DAYS = 180;

export type SurveyFreshnessType = 'strategy' | 'capabilities' | 'readiness';

export interface SurveyTypeFreshness {
  type: SurveyFreshnessType;
  label: string;
  lastUpdatedAt: Date | null;
  daysOld: number | null;
}

export interface SurveyFreshnessSummary {
  byType: SurveyTypeFreshness[];
  /** Most recent answer across strategy / capabilities / readiness. */
  lastUpdatedAt: Date | null;
  daysOld: number | null;
  /** Readiness-specific age (ticket signal). */
  readinessDaysOld: number | null;
  readinessLastUpdatedAt: Date | null;
  isReadinessStale: boolean;
}

const TYPE_LABELS: Record<SurveyFreshnessType, string> = {
  strategy: 'Strategy',
  capabilities: 'Capabilities',
  readiness: 'Data readiness',
};

/** Pull an ISO/date value out of BigQuery-style `{ value: "..." }` wrappers. */
export function coerceTimestamp(raw: unknown): Date | null {
  if (raw == null) return null;
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? null : raw;
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === 'string') {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if ('value' in obj) return coerceTimestamp(obj.value);
    if ('timestamp' in obj) return coerceTimestamp(obj.timestamp);
  }
  return null;
}

export function daysBetween(from: Date, to: Date = new Date()): number {
  const ms = to.getTime() - from.getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

export function formatDaysOld(daysOld: number): string {
  if (daysOld <= 0) return 'today';
  if (daysOld === 1) return '1 day ago';
  return `${daysOld} days ago`;
}

/** Latest timestamp on a single survey response row. */
export function extractRowTimestamp(row: unknown): Date | null {
  if (!row || typeof row !== 'object') return null;
  const r = row as Record<string, unknown>;
  return (
    coerceTimestamp(r.response_timestamp)
    || coerceTimestamp(r.updated_at)
    || coerceTimestamp(r.completed_at)
    || coerceTimestamp(r.created_at)
    || coerceTimestamp(r.timestamp)
    || coerceTimestamp(r.client_timestamp)
  );
}

/** Latest timestamp across an array of response rows. */
export function latestTimestampFromRows(rows: unknown[]): Date | null {
  let latest: Date | null = null;
  for (const row of rows) {
    const ts = extractRowTimestamp(row);
    if (!ts) continue;
    if (!latest || ts.getTime() > latest.getTime()) latest = ts;
  }
  return latest;
}

export function buildSurveyFreshnessSummary(
  timestamps: Partial<Record<SurveyFreshnessType, Date | null>>,
  now: Date = new Date(),
): SurveyFreshnessSummary {
  const types: SurveyFreshnessType[] = ['strategy', 'capabilities', 'readiness'];
  const byType: SurveyTypeFreshness[] = types.map((type) => {
    const lastUpdatedAt = timestamps[type] ?? null;
    return {
      type,
      label: TYPE_LABELS[type],
      lastUpdatedAt,
      daysOld: lastUpdatedAt ? daysBetween(lastUpdatedAt, now) : null,
    };
  });

  const withDates = byType.filter((t) => t.lastUpdatedAt != null) as Array<
    SurveyTypeFreshness & { lastUpdatedAt: Date; daysOld: number }
  >;
  const lastUpdatedAt =
    withDates.length > 0
      ? withDates.reduce((best, cur) =>
          cur.lastUpdatedAt.getTime() > best.lastUpdatedAt.getTime() ? cur : best,
        ).lastUpdatedAt
      : null;

  const readiness = byType.find((t) => t.type === 'readiness')!;
  const readinessDaysOld = readiness.daysOld;
  const isReadinessStale =
    readinessDaysOld != null && readinessDaysOld > SURVEY_READINESS_MAX_AGE_DAYS;

  return {
    byType,
    lastUpdatedAt,
    daysOld: lastUpdatedAt ? daysBetween(lastUpdatedAt, now) : null,
    readinessDaysOld,
    readinessLastUpdatedAt: readiness.lastUpdatedAt,
    isReadinessStale,
  };
}

export function surveyAgeSummaryLine(summary: SurveyFreshnessSummary): string | null {
  if (summary.daysOld == null) return null;
  return `Latest ${formatDaysOld(summary.daysOld)}`;
}

export function surveyAgeDetailLine(summary: SurveyFreshnessSummary): string | null {
  const parts = summary.byType
    .filter((t) => t.daysOld != null)
    .map((t) => `${shortSurveyLabel(t.type)} ${t.daysOld}d`);
  return parts.length ? parts.join(' · ') : null;
}

export function shortSurveyLabel(type: SurveyFreshnessType): string {
  if (type === 'strategy') return 'Strategy';
  if (type === 'capabilities') return 'Capabilities';
  return 'Readiness';
}

/** Compact label for a single module chip, e.g. "today" or "200d". */
export function surveyModuleAgeLabel(daysOld: number | null): string | null {
  if (daysOld == null) return null;
  if (daysOld <= 0) return 'today';
  if (daysOld === 1) return '1d';
  return `${daysOld}d`;
}

export function isSurveyModuleStale(daysOld: number | null): boolean {
  return daysOld != null && daysOld > SURVEY_READINESS_MAX_AGE_DAYS;
}

/** Ticket signal: readiness module older than max age. */
export function surveyReadinessStaleMessage(summary: SurveyFreshnessSummary): string | null {
  if (!summary.isReadinessStale || summary.readinessDaysOld == null) return null;
  return `Readiness stale · ${summary.readinessDaysOld}d (max ${SURVEY_READINESS_MAX_AGE_DAYS}d)`;
}

export interface SurveyCardStaleBadge {
  type: SurveyFreshnessType;
  label: string;
  daysOld: number;
}

/** "Last updated today" / "Last updated 12 days ago" for the Surveys card. */
export function surveyCardLastUpdatedLabel(summary: SurveyFreshnessSummary): string | null {
  if (summary.daysOld == null) return null;
  return `Last updated ${formatDaysOld(summary.daysOld)}`;
}

/** Amber status badges for modules older than 180 days. */
export function surveyCardStaleBadges(summary: SurveyFreshnessSummary): SurveyCardStaleBadge[] {
  return summary.byType
    .filter((row): row is SurveyTypeFreshness & { daysOld: number } =>
      isSurveyModuleStale(row.daysOld))
    .map((row) => ({
      type: row.type,
      label: `${shortSurveyLabel(row.type)} stale`,
      daysOld: row.daysOld,
    }));
}
